/**
 * Codes RD — administration page.
 *
 * ONE new sidebar entry, FOUR tabs inside:
 *   1. Registre & attribution   — visualise the imported registry +
 *                                 run the .xlsx import (checkable list
 *                                 of proposed matches, NO auto-approval)
 *   2. Rattrapage hired sans code — list hired candidates without a
 *                                 code_rd + apply attribution in bulk
 *   3. Jetons de service        — create/revoke Booking + Reporting
 *                                 API tokens
 *   4. Journal API              — read-only log of every /service/v1
 *                                 call
 *
 * Design language: matches the Embauche page (shariti-orange accent,
 * shariti-cream background, shariti-border cards, black/orange
 * uppercase-tracked headers).
 */
import { useEffect, useMemo, useRef, useState } from "react";

import {
    AlertTriangle,
    AlertCircle,
    ArrowRightLeft,
    CheckCircle2,
    Clock,
    Copy,
    Fingerprint,
    Flame,
    Loader2,
    RefreshCw,
    Scan,
    Search,
    ShieldAlert,
    Upload,
    UserCheck,
    History,
    FileText,
    ClipboardCopy,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import CodeRdExportButton from "../components/code-rd/CodeRdExportButton";


// ---------------------------------------------------------- shared helpers

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch {
        return "—";
    }
}


/**
 * Visual signature of a code_rd — used everywhere the code is
 * surfaced (fiche, Kanban card, export first column, admin lists).
 * Monospace + orange border makes it instantly recognisable amongst
 * regular text.
 */
export function CodeRdBadge({ code, size = "md" }) {
    if (!code) {
        return (
            <span
                data-testid="code-rd-badge-empty"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-400 border border-dashed border-slate-300"
            >
                Sans code
            </span>
        );
    }
    const sizing = size === "sm"
        ? "px-1.5 py-0.5 text-[10px]"
        : "px-2 py-0.5 text-[11px]";
    // Distinction visuelle CRITIQUE : ATO184 (lettre O, canonique Eric
    // Tutu Moparte) est surligné en vert pour ne JAMAIS être confondu
    // avec AT0184 (chiffre 0, reserved à vie).
    const isLiteralO = code === "ATO184";
    return (
        <span
            data-testid={`code-rd-badge-${code}`}
            title={isLiteralO
                ? "ATO184 · LETTRE O · exception nominative Eric Tutu Moparte (canonique à vie). Ne pas confondre avec AT0184 (chiffre 0, reserved à vie)."
                : `Matricule Shariti ${code}`}
            className={`inline-flex items-center gap-1 rounded-md font-mono font-black tracking-wider bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/60 ${sizing}`}
        >
            <Fingerprint className="w-3 h-3" />
            {isLiteralO ? (
                <span className="inline-flex items-baseline">
                    AT<span className="mx-[1px] px-1 rounded bg-emerald-500 text-white leading-none">O</span>184
                </span>
            ) : code}
        </span>
    );
}


// ---------------------------------------------------------- Tab 1: Registre & attribution

function RegistreTab({ counter, onRefreshCounter }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [q, setQ] = useState("");
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importing, setImporting] = useState(false);
    const [applying, setApplying] = useState(false);
    const [approvedSet, setApprovedSet] = useState(() => new Set());
    const fileInputRef = useRef(null);

    async function fetchRegistry() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/admin/code-rd/registry?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error("Impossible de charger le registre");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchRegistry(); }, [statusFilter]);

    async function onPreviewImport(file) {
        if (!file) return;
        setImporting(true);
        setImportPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/code-rd/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportPreview(data);
            // Preselect all proposed matches by default? NO — brief
            // says "aucune écriture sans mon clic" and "checkable list".
            // Start empty; the admin ticks what they approve.
            setApprovedSet(new Set());
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible de lire le fichier");
        } finally {
            setImporting(false);
        }
    }

    async function onApplyImport() {
        if (!importFile || !importPreview) return;
        const proposedCount = importPreview.proposed_matches?.length || 0;
        const approvedCount = approvedSet.size;
        const msg = `Confirmer l'import ?\n\n` +
            `- ${importPreview.attributed_count} codes historiques → table de réservation\n` +
            `- ${approvedCount}/${proposedCount} rapprochements approuvés → attribution directe\n` +
            `- ${importPreview.reserved_count + importPreview.cancelled_count} codes bloqués à vie\n` +
            `- Compteur démarré à AT${String(importPreview.would_start_counter_at).padStart(4, "0")}`;
        if (!window.confirm(msg)) return;

        setApplying(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            fd.append("approved_codes", Array.from(approvedSet).join(","));
            const { data } = await api.post("/admin/code-rd/import/apply", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `Import terminé : ${data.imported_registry_rows} lignes, ` +
                `${data.matched_candidates} rapprochement(s), compteur → AT${String(data.counter_start).padStart(4, "0")}`
            );
            setImportFile(null);
            setImportPreview(null);
            setApprovedSet(new Set());
            if (fileInputRef.current) fileInputRef.current.value = "";
            await fetchRegistry();
            await onRefreshCounter();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import échoué");
        } finally {
            setApplying(false);
        }
    }

    function toggleApproved(code) {
        setApprovedSet(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    }
    function toggleAll(select) {
        setApprovedSet(() => {
            if (!select) return new Set();
            const next = new Set();
            (importPreview?.proposed_matches || []).forEach(m => {
                if (!m.candidate_has_code) next.add(m.registry_code);
            });
            return next;
        });
    }

    const statusColors = {
        attributed: "bg-emerald-100 text-emerald-800",
        reserved: "bg-slate-200 text-slate-700",
        cancelled: "bg-rose-100 text-rose-800",
    };
    const statusLabels = {
        attributed: "Attribué",
        reserved: "Réservé",
        cancelled: "Annulé",
    };

    return (
        <div className="space-y-6" data-testid="code-rd-tab-registre">
            {/* Import panel */}
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                            Import du registre historique
                        </div>
                        <h2 className="text-lg font-black text-shariti-black">
                            Charger le fichier <span className="font-mono">.xlsx</span> fourni
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Aucune écriture tant que vous n'avez pas approuvé les rapprochements et validé.
                            Le compteur démarrera automatiquement au numéro suivant du plus grand code importé.
                        </p>
                    </div>
                    <div className="rounded-xl border border-shariti-orange/40 bg-shariti-orange-soft px-4 py-2 text-center min-w-[160px]">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-shariti-orange">Prochain code</div>
                        <div className="text-lg font-mono font-black text-shariti-orange" data-testid="code-rd-counter-badge">
                            {counter?.next_code || "AT0005"}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-shariti-orange/50 hover:border-shariti-orange bg-shariti-orange-soft/40 hover:bg-shariti-orange-soft cursor-pointer transition text-sm font-black text-shariti-orange uppercase tracking-wider">
                        <Upload className="w-4 h-4" />
                        {importFile ? importFile.name : "Choisir un fichier .xlsx"}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            data-testid="code-rd-import-input"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                setImportFile(f || null);
                                setImportPreview(null);
                                if (f) onPreviewImport(f);
                            }}
                        />
                    </label>
                    {importing && (
                        <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Analyse du fichier…
                        </span>
                    )}
                    {/* Apply button is ALWAYS rendered (disabled until a
                        file is loaded + a preview is available). Keeps
                        the DOM contract stable for automated tests and
                        signals to the admin what the next step is. */}
                    <button
                        type="button"
                        onClick={onApplyImport}
                        disabled={!importPreview || applying}
                        data-testid="code-rd-import-apply"
                        className="ml-auto px-4 py-2 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-black uppercase tracking-wider transition flex items-center gap-2"
                    >
                        {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Approuver la sélection & importer
                    </button>
                </div>

                {importPreview && (
                    <div className="mt-5 space-y-4">
                        <div className="grid grid-cols-4 gap-3 text-center">
                            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Attribués</div>
                                <div className="text-lg font-black text-emerald-800">{importPreview.attributed_count}</div>
                            </div>
                            <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Réservés</div>
                                <div className="text-lg font-black text-slate-700">{importPreview.reserved_count}</div>
                            </div>
                            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Annulés</div>
                                <div className="text-lg font-black text-rose-800">{importPreview.cancelled_count}</div>
                            </div>
                            <div className="rounded-lg bg-shariti-orange-soft border border-shariti-orange/40 px-3 py-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-shariti-orange">Compteur</div>
                                <div className="text-lg font-mono font-black text-shariti-orange">
                                    AT{String(importPreview.would_start_counter_at).padStart(4, "0")}
                                </div>
                            </div>
                        </div>

                        {importPreview.already_imported && (
                            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>
                                    Le registre a déjà été importé. Un nouvel import mettra à jour les
                                    lignes existantes (upsert) sans supprimer les rapprochements existants.
                                </span>
                            </div>
                        )}

                        {/* Proposed matches - checkable list */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                                    Rapprochements proposés ({importPreview.proposed_matches.length})
                                </div>
                                {importPreview.proposed_matches.length > 0 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <button
                                            type="button"
                                            className="text-shariti-orange hover:underline font-black uppercase tracking-wider"
                                            data-testid="code-rd-select-all"
                                            onClick={() => toggleAll(true)}
                                        >
                                            Tout cocher
                                        </button>
                                        <span className="text-slate-300">·</span>
                                        <button
                                            type="button"
                                            className="text-slate-500 hover:underline font-black uppercase tracking-wider"
                                            onClick={() => toggleAll(false)}
                                        >
                                            Aucun
                                        </button>
                                    </div>
                                )}
                            </div>
                            {importPreview.proposed_matches.length === 0 ? (
                                <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                                    Aucun candidat PREREC ne correspond à un nom du registre.
                                    C'est le comportement attendu — les {importPreview.attributed_count} codes attribués
                                    seront placés en table de réservation nominative et associés automatiquement
                                    lors du prochain onboarding d'une personne connue.
                                </div>
                            ) : (
                                <div className="rounded-lg border border-shariti-border bg-white divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
                                    {importPreview.proposed_matches.map((m) => {
                                        const checked = approvedSet.has(m.registry_code);
                                        const conflict = m.candidate_has_code;
                                        return (
                                            <label
                                                key={`${m.registry_code}-${m.candidate_id}`}
                                                className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer ${conflict ? "opacity-60" : ""}`}
                                                data-testid={`code-rd-match-row-${m.registry_code}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={conflict}
                                                    onChange={() => toggleApproved(m.registry_code)}
                                                    className="w-4 h-4 accent-shariti-orange"
                                                />
                                                <CodeRdBadge code={m.registry_code} size="sm" />
                                                <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            Nom historique (registre)
                                                        </div>
                                                        <div className="text-slate-800 font-semibold">{m.historical_name}</div>
                                                        {m.historical_prefix && (
                                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                                ancien préfixe : {m.historical_prefix}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            Candidat PREREC
                                                        </div>
                                                        <div className="text-slate-800 font-semibold">{m.candidate_full_name}</div>
                                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                                            {m.candidate_email} · {m.candidate_status}
                                                        </div>
                                                    </div>
                                                </div>
                                                {conflict && (
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-rose-600 flex items-center gap-1">
                                                        <ShieldAlert className="w-3 h-3" /> déjà un code
                                                    </span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                            <div className="text-xs text-slate-500">
                                {approvedSet.size} rapprochement(s) coché(s) · {importPreview.attributed_count - approvedSet.size} en table de réservation
                            </div>
                            <button
                                type="button"
                                onClick={() => { setImportFile(null); setImportPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                                className="px-4 py-2 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-black uppercase tracking-wider transition"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* Registry list */}
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                            Registre en base ({total})
                        </div>
                        <p className="text-sm text-slate-500">Toutes les entrées importées avec leur statut et rapprochement candidat.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && fetchRegistry()}
                                placeholder="Chercher AT0272 ou un nom…"
                                data-testid="code-rd-registry-search"
                                className="pl-9 pr-3 py-2 rounded-full border border-shariti-border bg-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange transition"
                            />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="code-rd-registry-status-filter"
                            className="px-3 py-2 rounded-full border border-shariti-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange transition"
                        >
                            <option value="">Tous les statuts</option>
                            <option value="attributed">Attribués</option>
                            <option value="reserved">Réservés</option>
                            <option value="cancelled">Annulés</option>
                        </select>
                        <button
                            type="button"
                            onClick={fetchRegistry}
                            className="p-2 rounded-full border border-shariti-border bg-white hover:bg-slate-50 transition"
                            title="Actualiser"
                        >
                            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
                        </button>
                        <CodeRdExportButton />
                    </div>
                </div>

                {loading && items.length === 0 ? (
                    <div className="py-12 text-center text-sm text-slate-400">Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                        Le registre est vide. Utilisez l'import ci-dessus pour charger le fichier .xlsx.
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-shariti-border">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Code</th>
                                    <th className="px-3 py-2 text-left">Nom historique</th>
                                    <th className="px-3 py-2 text-left">Statut</th>
                                    <th className="px-3 py-2 text-left">Ancien préfixe</th>
                                    <th className="px-3 py-2 text-left">Rattaché</th>
                                    <th className="px-3 py-2 text-left">Observation</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((r) => (
                                    <tr key={r.code} className="hover:bg-slate-50">
                                        <td className="px-3 py-2"><CodeRdBadge code={r.code} size="sm" /></td>
                                        <td className="px-3 py-2 text-slate-700">
                                            {[r.prenom_historique, r.nom_historique].filter(Boolean).join(" ") || <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusColors[r.statut] || "bg-slate-100 text-slate-700"}`}>
                                                {statusLabels[r.statut] || r.statut}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-xs">
                                            {r.ancien_prefixe || <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-xs">
                                            {r.candidate_id ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                                    <UserCheck className="w-3 h-3" /> {fmtDate(r.matched_at)}
                                                </span>
                                            ) : <span className="text-slate-300">non rattaché</span>}
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-xs max-w-md truncate" title={r.observation}>
                                            {r.observation || <span className="text-slate-300">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}


// ---------------------------------------------------------- Tab 2: Rattrapage hired sans code

function RattrapageTab({ onRefreshCounter, onGoToArbitrage }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [applying, setApplying] = useState(false);

    async function fetchBackfill() {
        setLoading(true);
        try {
            const { data: resp } = await api.get("/admin/code-rd/backfill/hired");
            setData(resp);
            setSelected(new Set());
        } catch (err) {
            toast.error("Impossible de charger la liste");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { fetchBackfill(); }, []);

    function toggle(id) {
        setSelected(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    }
    function toggleAll(select, group) {
        setSelected(() => {
            if (!select) return new Set();
            const n = new Set();
            group.forEach(c => n.add(c.candidate_id));
            return n;
        });
    }

    async function onApply() {
        if (selected.size === 0) return;
        if (!window.confirm(`Confirmer l'attribution de ${selected.size} code(s) RD ?\n\nLes candidats correspondant à un nom du registre historique récupéreront leur code d'origine. Les autres recevront un code automatique.`)) return;
        setApplying(true);
        try {
            const { data: resp } = await api.post("/admin/code-rd/backfill/hired/apply", {
                candidate_ids: Array.from(selected),
            });
            const ok = resp.attributions.filter(a => a.ok).length;
            const pending = resp.attributions.filter(a => a.reason === "pending_review").length;
            const failed = resp.attributions.filter(a => !a.ok && a.reason !== "pending_review").length;

            if (ok > 0) {
                toast.success(`${ok} attribution(s) réussie(s)`);
            }
            if (pending > 0) {
                toast.warning(
                    `${pending} candidat(s) en attente d'arbitrage — aucun code attribué. ` +
                    "Résous l'ambiguïté dans l'onglet Arbitrage.",
                    { duration: 8000, action: onGoToArbitrage ? {
                        label: "Ouvrir l'arbitrage",
                        onClick: () => onGoToArbitrage(),
                    } : undefined },
                );
            }
            if (failed > 0) {
                const firstErr = resp.attributions.find(a => !a.ok && a.reason !== "pending_review");
                toast.error(`${failed} attribution(s) échouée(s) — ${firstErr?.error || "voir logs"}`);
            }
            if (ok === 0 && pending === 0 && failed === 0) {
                toast.info("Aucune attribution — sélection vide ?");
            }
            await fetchBackfill();
            await onRefreshCounter();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Attribution échouée");
        } finally {
            setApplying(false);
        }
    }

    const withHist = data?.with_historical_match || [];
    const withoutHist = data?.without_historical_match || [];
    const arbitrationPending = data?.arbitration_pending || [];

    function renderGroup(title, subtitle, tone, group, opts = {}) {
        if (group.length === 0) return null;
        const isPending = opts.pending === true;
        return (
            <div className={`rounded-xl border p-4 ${
                isPending ? "bg-amber-50 border-amber-200"
                          : "bg-white border-shariti-border"
            }`}>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${
                            isPending ? "text-amber-700"
                                      : tone === "hist" ? "text-emerald-700" : "text-shariti-orange"
                        }`}>
                            {title} ({group.length})
                        </div>
                        <p className="text-xs text-slate-500">{subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        {isPending && onGoToArbitrage ? (
                            <button
                                onClick={onGoToArbitrage}
                                data-testid="rattrapage-goto-arbitrage"
                                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 font-black uppercase tracking-wider text-[10px]"
                            >
                                Ouvrir l&apos;arbitrage →
                            </button>
                        ) : (
                            <button
                                className="text-shariti-orange hover:underline font-black uppercase tracking-wider"
                                onClick={() => toggleAll(true, group)}
                            >
                                Tout cocher
                            </button>
                        )}
                    </div>
                </div>
                <div className="divide-y divide-slate-100">
                    {group.map(c => (
                        <label
                            key={c.candidate_id}
                            className={`flex items-center gap-3 px-2 py-2.5 ${
                                isPending ? "cursor-not-allowed opacity-90"
                                          : "hover:bg-slate-50 cursor-pointer"
                            }`}
                        >
                            {isPending ? (
                                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                            ) : (
                                <input
                                    type="checkbox"
                                    checked={selected.has(c.candidate_id)}
                                    onChange={() => toggle(c.candidate_id)}
                                    className="w-4 h-4 accent-shariti-orange"
                                    data-testid={`code-rd-rattrapage-check-${c.candidate_id}`}
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-800 truncate">{c.full_name}</div>
                                <div className="text-[11px] text-slate-500">
                                    {c.email} · embauché {fmtDate(c.hired_at)}
                                    {isPending && c.pending_proposals?.length > 0 && (
                                        <> · candidats possibles : <b>{c.pending_proposals.join(", ")}</b></>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-0.5">
                                {isPending ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider">
                                        Arbitrage requis
                                    </span>
                                ) : (
                                    <>
                                        <CodeRdBadge code={c.proposed_code} size="sm" />
                                        <span className="text-[10px] text-slate-400">
                                            {c.proposed_source === "historical" ? "historique" : "auto"}
                                        </span>
                                    </>
                                )}
                            </div>
                        </label>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="code-rd-tab-rattrapage">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                        Rattrapage · candidats hired sans code
                    </div>
                    <p className="text-sm text-slate-500">
                        Total à rattraper : <strong>{data?.total_hired_without_code ?? "—"}</strong> collaborateur(s).
                        Les codes proposés respectent le registre historique en priorité.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={fetchBackfill}
                        className="p-2 rounded-full border border-shariti-border bg-white hover:bg-slate-50 transition"
                        title="Actualiser"
                    >
                        <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
                    </button>
                    <button
                        type="button"
                        onClick={onApply}
                        disabled={applying || selected.size === 0}
                        data-testid="code-rd-rattrapage-apply"
                        className="px-4 py-2 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider transition flex items-center gap-2"
                    >
                        {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Attribuer {selected.size > 0 ? `(${selected.size})` : ""}
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="py-12 text-center text-sm text-slate-400">Chargement…</div>
            ) : (data?.total_hired_without_code || 0) === 0 ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-8 text-center text-sm text-emerald-800">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                    Tous les candidats hired ont déjà un code RD. Rien à rattraper.
                </div>
            ) : (
                <div className="space-y-4">
                    {renderGroup(
                        "⚠️ Arbitrage requis — attribution bloquée",
                        "Ces candidats matchent plusieurs codes historiques (fuzzy). Aucun code ne sera attribué automatiquement — bascule dans l'onglet Arbitrage pour choisir.",
                        "pending",
                        arbitrationPending,
                        { pending: true },
                    )}
                    {renderGroup(
                        "Codes historiques disponibles",
                        "Le nom correspond à une entrée du registre. Ces candidats récupèrent leur code d'origine.",
                        "hist",
                        withHist,
                    )}
                    {renderGroup(
                        "Nouveaux codes (générés par le compteur)",
                        "Aucun rapprochement historique. Un code AT + 4 chiffres consécutif sera attribué.",
                        "auto",
                        withoutHist,
                    )}
                </div>
            )}
        </div>
    );
}


// ---------------------------------------------------------- Tab 3: Jetons de service

function TokensTab() {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [freshToken, setFreshToken] = useState(null);   // {token, name} shown ONCE after creation

    async function fetchTokens() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/service-tokens");
            setTokens(data || []);
        } catch (err) {
            toast.error("Impossible de charger les jetons");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { fetchTokens(); }, []);

    async function onCreate() {
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        try {
            const { data } = await api.post("/admin/service-tokens", { name });
            setFreshToken({ token: data.token, name: data.name });
            setNewName("");
            await fetchTokens();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Création échouée");
        } finally {
            setCreating(false);
        }
    }

    async function onRevoke(t) {
        if (!window.confirm(`Révoquer le jeton "${t.name}" (…${t.last4}) ?\nLes appels Booking/Reporting utilisant ce jeton seront refusés immédiatement.`)) return;
        try {
            await api.post(`/admin/service-tokens/${t.id}/revoke`);
            toast.success("Jeton révoqué");
            await fetchTokens();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Révocation échouée");
        }
    }

    return (
        <div className="space-y-4" data-testid="code-rd-tab-tokens">
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                    Créer un jeton
                </div>
                <p className="text-sm text-slate-500 mb-3">
                    Un jeton par plateforme consommatrice (Booking, Reporting). Le jeton en clair
                    s'affiche <strong>une seule fois</strong> après création — copiez-le immédiatement.
                </p>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && onCreate()}
                        placeholder="booking · reporting · …"
                        data-testid="code-rd-token-name-input"
                        className="flex-1 px-4 py-2 rounded-full border border-shariti-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange transition"
                    />
                    <button
                        type="button"
                        onClick={onCreate}
                        disabled={creating || !newName.trim()}
                        data-testid="code-rd-token-create"
                        className="px-4 py-2 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider transition"
                    >
                        {creating ? "Création…" : "Créer"}
                    </button>
                </div>
            </section>

            {freshToken && (
                <section className="rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-5" data-testid="code-rd-fresh-token">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">
                                Jeton "{freshToken.name}" — Copiez-le maintenant, il ne sera plus jamais affiché
                            </div>
                            <div className="font-mono text-sm bg-white rounded-lg border border-emerald-200 px-3 py-2 mt-2 break-all select-all">
                                {freshToken.token}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(freshToken.token); toast.success("Jeton copié"); }}
                                className="px-3 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider transition flex items-center gap-1"
                            >
                                <Copy className="w-3.5 h-3.5" /> Copier
                            </button>
                            <button
                                type="button"
                                onClick={() => setFreshToken(null)}
                                className="px-3 py-2 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs font-black uppercase tracking-wider transition flex items-center gap-1"
                            >
                                <X className="w-3.5 h-3.5" /> J'ai copié
                            </button>
                        </div>
                    </div>
                </section>
            )}

            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-4">
                    Jetons actifs ({tokens.filter(t => !t.revoked_at).length}) · révoqués ({tokens.filter(t => t.revoked_at).length})
                </div>
                {loading && tokens.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Chargement…</div>
                ) : tokens.length === 0 ? (
                    <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                        Aucun jeton créé pour le moment.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {tokens.map(t => (
                            <div key={t.id} className={`flex items-center gap-3 py-3 ${t.revoked_at ? "opacity-50" : ""}`}>
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                                    <div className="text-[11px] text-slate-500 font-mono">
                                        …{t.last4} · créé {fmtDate(t.created_at)}
                                        {t.last_used_at && ` · dernier appel ${fmtDate(t.last_used_at)}`}
                                        {t.revoked_at && ` · révoqué ${fmtDate(t.revoked_at)}`}
                                    </div>
                                </div>
                                {!t.revoked_at && (
                                    <button
                                        type="button"
                                        onClick={() => onRevoke(t)}
                                        data-testid={`code-rd-token-revoke-${t.id}`}
                                        className="px-3 py-1.5 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-black uppercase tracking-wider transition"
                                    >
                                        Révoquer
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}


// ---------------------------------------------------------- Tab 4: Journal API

function JournalTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [consumer, setConsumer] = useState("");

    async function fetchCalls() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (consumer.trim()) params.set("consumer", consumer.trim());
            const { data } = await api.get(`/admin/service-tokens/calls?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error("Impossible de charger le journal");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { fetchCalls(); }, []);

    return (
        <div className="space-y-4" data-testid="code-rd-tab-journal">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                        Journal API — {total} appel(s) enregistré(s)
                    </div>
                    <p className="text-sm text-slate-500">
                        Trace de chaque appel à <span className="font-mono">/api/service/v1/*</span> par les plateformes consommatrices.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={consumer}
                        onChange={(e) => setConsumer(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchCalls()}
                        placeholder="Filtrer par consommateur…"
                        className="px-3 py-2 rounded-full border border-shariti-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange transition"
                    />
                    <button
                        type="button"
                        onClick={fetchCalls}
                        className="p-2 rounded-full border border-shariti-border bg-white hover:bg-slate-50 transition"
                        title="Actualiser"
                    >
                        <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            <div className="rounded-2xl bg-white border border-shariti-border overflow-hidden">
                {loading && items.length === 0 ? (
                    <div className="py-12 text-center text-sm text-slate-400">Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-500">
                        Aucun appel enregistré. Le journal se peuplera dès que Booking ou Reporting appelleront l'API.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Horodatage</th>
                                <th className="px-3 py-2 text-left">Consommateur</th>
                                <th className="px-3 py-2 text-left">Méthode</th>
                                <th className="px-3 py-2 text-left">Route</th>
                                <th className="px-3 py-2 text-right">Statut</th>
                                <th className="px-3 py-2 text-right">Réponse</th>
                                <th className="px-3 py-2 text-right">Durée</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map(c => (
                                <tr key={c.id} className="hover:bg-slate-50">
                                    <td className="px-3 py-2 text-slate-600 text-xs whitespace-nowrap">
                                        {new Date(c.ts).toLocaleString("fr-FR")}
                                    </td>
                                    <td className="px-3 py-2 text-slate-800 font-semibold">{c.consumer_name}</td>
                                    <td className="px-3 py-2 text-slate-500 font-mono text-xs">{c.method}</td>
                                    <td className="px-3 py-2 text-slate-500 font-mono text-xs">
                                        {c.route}{c.query_string ? `?${c.query_string}` : ""}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider ${c.status_code < 400 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                                            {c.status_code}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right text-slate-500 text-xs font-mono">
                                        {c.response_bytes ? `${Math.round(c.response_bytes/1024)} Ko` : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right text-slate-500 text-xs font-mono">
                                        {c.duration_ms != null ? `${c.duration_ms} ms` : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


// ---------------------------------------------------------- Tab 5: Pending Review (Passe C · Volet 2)

function PendingReviewTab({ onRefreshCounter }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResolved, setShowResolved] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanProposals, setScanProposals] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [applying, setApplying] = useState(false);
    const [fixingAto184, setFixingAto184] = useState(false);
    const [ato184Result, setAto184Result] = useState(null);
    const [coherenceLoading, setCoherenceLoading] = useState(false);
    const [coherenceData, setCoherenceData] = useState(null);

    async function fetchTickets() {
        setLoading(true);
        try {
            const url = showResolved
                ? "/admin/code-rd/pending?status=validated"
                : "/admin/code-rd/pending";
            const { data } = await api.get(url);
            setTickets(data.items || []);
        } catch (err) {
            toast.error("Impossible de charger la file d'attente");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchTickets(); }, [showResolved]);

    async function onValidate(ticket, code) {
        const note = window.prompt(
            `Confirmer l'attribution du code ${code} à ${ticket.target_first_name} ${ticket.target_last_name} ?\n\nMotif de la décision (optionnel) :`,
            "",
        );
        if (note === null) return;
        try {
            await api.post(`/admin/code-rd/pending/${ticket.id}/validate`, {
                chosen_code: code,
                admin_note: note || null,
            });
            toast.success(`${code} attribué à ${ticket.target_first_name} ${ticket.target_last_name}`);
            await fetchTickets();
            await onRefreshCounter();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Attribution échouée");
        }
    }

    async function onReject(ticket) {
        const note = window.prompt(
            `Refuser toutes les propositions pour ${ticket.target_first_name} ${ticket.target_last_name} ?\n\nMotif (optionnel) — le candidat restera SANS code jusqu'à un rattrapage manuel :`,
            "",
        );
        if (note === null) return;
        try {
            await api.post(`/admin/code-rd/pending/${ticket.id}/reject`, {
                admin_note: note || null,
            });
            toast.success("Propositions refusées · candidat sans code");
            await fetchTickets();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refus échoué");
        }
    }

    async function onMint(ticket) {
        const ok = window.confirm(
            `Minter un NOUVEAU code pour ${ticket.target_first_name} ${ticket.target_last_name} ?\n\nCette action refuse toutes les propositions historiques et incrémente le compteur.`,
        );
        if (!ok) return;
        try {
            const { data } = await api.post(`/admin/code-rd/pending/${ticket.id}/mint`, {
                admin_note: "Mint volontaire — vrai nouveau collaborateur",
            });
            toast.success(`Nouveau code ${data.resolved_code} minté`);
            await fetchTickets();
            await onRefreshCounter();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Mint échoué");
        }
    }

    const keyOf = (p) => `${p.target_kind}:${p.target_id}:${p.proposed_code}`;

    async function onScan() {
        setScanning(true);
        setScanProposals(null);
        setSelectedKeys(new Set());
        try {
            const { data } = await api.post("/admin/code-rd/rematch/scan");
            setScanProposals(data);
            // Tout coché par défaut — l'admin décoche ce qu'il ne veut pas.
            setSelectedKeys(new Set((data.proposals || []).map(keyOf)));
            if (data.total_scanned === 0) {
                toast.success("Rien à rematcher — tous les candidats sans code sont sans match historique");
            } else {
                toast.success(`${data.exact_matches} exacts + ${data.fuzzy_matches} fuzzy détectés`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Scan échoué");
        } finally {
            setScanning(false);
        }
    }

    function toggleSelected(key) {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }
    function selectAll() {
        setSelectedKeys(new Set((scanProposals?.proposals || []).map(keyOf)));
    }
    function selectNone() {
        setSelectedKeys(new Set());
    }

    async function onApplyRematch() {
        if (!scanProposals) return;
        const selected = scanProposals.proposals.filter(p => selectedKeys.has(keyOf(p)));
        if (selected.length === 0) {
            toast.error("Aucune ligne sélectionnée");
            return;
        }
        if (!window.confirm(`Appliquer la sélection : ${selected.length} rattachement(s) ?\n\nLes codes RESERVED (ex. AT0184) seront refusés côté serveur.`)) return;
        setApplying(true);
        try {
            const proposals = selected.map(p => ({
                target_kind: p.target_kind,
                target_id: p.target_id,
                proposed_code: p.proposed_code,
            }));
            const { data } = await api.post("/admin/code-rd/rematch/apply", { proposals });
            const ok = data.results.filter(r => r.ok).length;
            const ko = data.results.filter(r => !r.ok);
            if (ko.length > 0) {
                toast.error(`${ok} OK · ${ko.length} en erreur — cf. panneau détaillé`);
            } else {
                toast.success(`${ok} rattachement(s) appliqué(s)`);
            }
            // Garde le panneau ouvert pour montrer les résultats
            setScanProposals(prev => ({ ...prev, apply_results: data.results }));
            await fetchTickets();
            await onRefreshCounter();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Apply échoué");
        } finally {
            setApplying(false);
        }
    }

    async function onCorrigerAto184() {
        const ok = window.confirm(
            "Corriger ATO184 (idempotent) ?\n\n" +
            "→ Ligne registre Eric Tutu Moparte alignée sur ATO184 (lettre O, canonique)\n" +
            "→ Entrée AT0184 (chiffre 0) mise en statut `reserved` à vie\n\n" +
            "Second clic prouvera changed=false."
        );
        if (!ok) return;
        setFixingAto184(true);
        setAto184Result(null);
        try {
            const { data } = await api.post("/admin/code-rd/migrate/ato184-fixture");
            setAto184Result(data);
            if (data.changed) {
                toast.success("ATO184 corrigé · AT0184 → reserved. Re-scanne le rematch.");
            } else {
                toast.success("Idempotent — registre déjà conforme (changed=false)");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Migration échouée");
        } finally {
            setFixingAto184(false);
        }
    }

    async function onBalayageCoherence() {
        setCoherenceLoading(true);
        setCoherenceData(null);
        try {
            const { data } = await api.get("/admin/code-rd/diagnostics/coherence");
            setCoherenceData(data);
            const dup = data.duplicate_holders?.length || 0;
            const holes = data.holes_unexpected?.count || 0;
            const orphans = (data.holders_above_545 || []).filter(h => h.entity === "ORPHAN").length;
            if (dup === 0 && orphans === 0) {
                toast.success(`Cohérence OK · ${holes} trou(s) résiduel(s), 0 doublon, 0 orphelin`);
            } else {
                toast.error(`Anomalies : ${dup} doublon(s), ${orphans} orphelin(s)`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Balayage échoué");
        } finally {
            setCoherenceLoading(false);
        }
    }

    const confidenceColor = {
        high: "bg-emerald-100 text-emerald-800 border-emerald-300",
        medium: "bg-amber-100 text-amber-800 border-amber-300",
        low: "bg-rose-100 text-rose-800 border-rose-300",
    };

    return (
        <div className="space-y-6" data-testid="code-rd-tab-pending">
            {/* Rematch scan section — Volet 1 */}
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                            Passe C · Volet 1 — Rematch rétroactif
                        </div>
                        <h2 className="text-lg font-black text-shariti-black">Scanner les candidats sans code</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Repasse TOUS les candidats/permanents sans code_rd contre le registre historique.
                            Les matches exacts se rattachent automatiquement, les fuzzy se rattachent aussi,
                            et les cas AMBIGUS (plusieurs matches proches sans winner clair) finissent en
                            file d'attente ci-dessous — pas de mint silencieux.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onScan}
                        disabled={scanning}
                        data-testid="code-rd-rematch-scan"
                        className="px-4 py-2 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider transition flex items-center gap-2"
                    >
                        {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                        Scanner
                    </button>
                </div>

                {scanProposals && (
                    <div className="rounded-lg bg-slate-50 border border-shariti-border p-3">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="text-sm font-semibold text-slate-800">
                                {scanProposals.total_scanned} match(es) : {scanProposals.exact_matches} exact + {scanProposals.fuzzy_matches} fuzzy
                                {" · "}
                                <span className="text-shariti-orange">{selectedKeys.size} sélectionné(s)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={selectAll}
                                    data-testid="code-rd-select-all"
                                    className="px-2 py-1 rounded-full border border-shariti-border bg-white text-[10px] font-black uppercase tracking-wider hover:bg-slate-50"
                                >Tout cocher</button>
                                <button type="button" onClick={selectNone}
                                    data-testid="code-rd-select-none"
                                    className="px-2 py-1 rounded-full border border-shariti-border bg-white text-[10px] font-black uppercase tracking-wider hover:bg-slate-50"
                                >Tout décocher</button>
                                <button
                                    type="button"
                                    onClick={onApplyRematch}
                                    disabled={applying || selectedKeys.size === 0}
                                    data-testid="code-rd-rematch-apply"
                                    className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[11px] font-black uppercase tracking-wider transition flex items-center gap-1"
                                >
                                    {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                    Appliquer la sélection ({selectedKeys.size})
                                </button>
                            </div>
                        </div>
                        <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
                            {scanProposals.proposals.map((p, idx) => {
                                const key = keyOf(p);
                                const checked = selectedKeys.has(key);
                                const applyResult = (scanProposals.apply_results || []).find(
                                    r => r.target_id === p.target_id && (r.code === p.proposed_code || r.error)
                                );
                                return (
                                    <label
                                        key={key}
                                        htmlFor={`prop-${idx}`}
                                        className={`py-2 flex items-center gap-3 cursor-pointer hover:bg-white/60 transition rounded px-2 ${applyResult?.ok ? "bg-emerald-50" : applyResult && !applyResult.ok ? "bg-rose-50" : ""}`}
                                        data-testid={`code-rd-proposal-${p.target_id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            id={`prop-${idx}`}
                                            checked={checked}
                                            onChange={() => toggleSelected(key)}
                                            data-testid={`code-rd-proposal-check-${p.target_id}`}
                                            className="w-4 h-4 accent-shariti-orange cursor-pointer"
                                            disabled={applyResult?.ok}
                                        />
                                        <CodeRdBadge code={p.proposed_code} size="sm" />
                                        <div className="flex-1">
                                            <div className="text-sm text-slate-800 font-semibold">{p.target_name}</div>
                                            <div className="text-[11px] text-slate-500">
                                                {p.target_kind} · {p.match_type} · historique : {p.historical_name}
                                            </div>
                                            {applyResult && !applyResult.ok && (
                                                <div className="text-[11px] text-rose-700 font-semibold mt-1">
                                                    ❌ {applyResult.error}
                                                </div>
                                            )}
                                            {applyResult?.ok && (
                                                <div className="text-[11px] text-emerald-700 font-semibold mt-1">
                                                    ✅ Attribué · {applyResult.code}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>

            {/* Correction ATO184 + Balayage cohérence — nouveaux outils prod */}
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="mb-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                        Passe C · Outils registre
                    </div>
                    <h2 className="text-lg font-black text-shariti-black">Correction & balayage</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        <strong>Corriger ATO184</strong> aligne la ligne registre d'Eric Tutu Moparte sur son code
                        canonique historique (lettre O) et met AT0184 (chiffre 0) en <code>reserved</code> à vie.
                        <strong> Balayage cohérence</strong> vérifie qu'aucun code n'a deux détenteurs et liste
                        les trous anormaux dans le compteur.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                        type="button"
                        onClick={onCorrigerAto184}
                        disabled={fixingAto184}
                        data-testid="code-rd-fix-ato184"
                        className="px-4 py-2 rounded-full bg-shariti-black hover:bg-shariti-black/85 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider transition flex items-center gap-2"
                    >
                        {fixingAto184 ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                        Corriger ATO184
                    </button>
                    <button
                        type="button"
                        onClick={onBalayageCoherence}
                        disabled={coherenceLoading}
                        data-testid="code-rd-coherence-scan"
                        className="px-4 py-2 rounded-full border border-shariti-border bg-white hover:bg-slate-50 disabled:opacity-40 text-shariti-black text-xs font-black uppercase tracking-wider transition flex items-center gap-2"
                    >
                        {coherenceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Balayage cohérence
                    </button>
                </div>

                {ato184Result && (
                    <div className={`rounded-lg border p-3 mb-3 ${ato184Result.changed ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-shariti-border"}`}>
                        <div className="text-xs font-black uppercase tracking-widest mb-2">
                            {ato184Result.changed ? "✅ Changé" : "⏸ Idempotent — changed=false"}
                        </div>
                        <div className="text-xs text-slate-700 space-y-1">
                            <div><strong>ATO184</strong> · avant: statut={ato184Result.ato184.before.statut || "absent"}, name_key={ato184Result.ato184.before.name_key || "—"}</div>
                            <div><strong>ATO184</strong> · après: statut={ato184Result.ato184.after.statut}, name_key={ato184Result.ato184.after.name_key}</div>
                            <div className="pt-1"><strong>AT0184</strong> · avant: statut={ato184Result.at0184.before.statut || "absent"}</div>
                            <div><strong>AT0184</strong> · après: statut={ato184Result.at0184.after.statut}</div>
                        </div>
                    </div>
                )}

                {coherenceData && (
                    <div className="rounded-lg border border-shariti-border bg-slate-50 p-3 space-y-3">
                        <div>
                            <div className="text-xs font-black uppercase tracking-widest mb-1">Compteur</div>
                            <div className="text-xs text-slate-700">
                                next_actual = {coherenceData.coherence_next.next_actual} · next_expected = {coherenceData.coherence_next.next_expected} · delta = {coherenceData.coherence_next.delta}
                                {coherenceData.coherence_next.ok ? " ✅ OK" : " ❌ INCOHÉRENT"}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-black uppercase tracking-widest mb-1">
                                Détenteurs ≥ AT0545 ({(coherenceData.holders_above_545 || []).length})
                            </div>
                            <div className="max-h-40 overflow-y-auto text-[11px] text-slate-700 font-mono">
                                {(coherenceData.holders_above_545 || []).map((h, i) => (
                                    <div key={i} className={h.entity === "ORPHAN" ? "text-rose-700 font-black" : ""}>
                                        {h.code} · {h.entity} · {h.full_name || "—"} · mode={h.mode || "—"} · test={String(h.is_test)}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-black uppercase tracking-widest mb-1">
                                Doublons ({(coherenceData.duplicate_holders || []).length})
                            </div>
                            {(coherenceData.duplicate_holders || []).length === 0 ? (
                                <div className="text-xs text-emerald-700">✅ Aucun doublon</div>
                            ) : (
                                <div className="text-[11px] text-rose-700 font-mono">
                                    {JSON.stringify(coherenceData.duplicate_holders, null, 2)}
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-xs font-black uppercase tracking-widest mb-1">
                                Trous anormaux ({coherenceData.holes_unexpected.count})
                            </div>
                            <div className="text-[11px] text-slate-500 mb-1">
                                Trous légitimes exclus : 0091-0099, 0410-0499, AT0184, AT0537, AT0541.
                            </div>
                            {coherenceData.holes_unexpected.count === 0 ? (
                                <div className="text-xs text-emerald-700">✅ Aucun trou anormal</div>
                            ) : (
                                <div className="text-[11px] text-amber-800 font-mono flex flex-wrap gap-1">
                                    {coherenceData.holes_unexpected.codes.map(c => (
                                        <span key={c} className="px-1.5 py-0.5 bg-white border border-amber-300 rounded">{c}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>

            {/* Pending tickets — Volet 2 */}
            <section className="rounded-2xl bg-white border border-shariti-border p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                            Passe C · Volet 2 — File d'attente d'arbitrage
                        </div>
                        <h2 className="text-lg font-black text-shariti-black">
                            {tickets.length} ticket(s) {showResolved ? "résolu(s)" : "à arbitrer"}
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Ces candidats ont des matches historiques PROCHES mais aucun winner clair.
                            L'algorithme propose — l'humain décide.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowResolved(v => !v)}
                            className="px-3 py-1.5 rounded-full border border-shariti-border bg-white hover:bg-slate-50 text-xs font-black uppercase tracking-wider transition"
                            data-testid="code-rd-toggle-resolved"
                        >
                            {showResolved ? "Voir ouverts" : "Voir résolus"}
                        </button>
                        <button
                            type="button"
                            onClick={fetchTickets}
                            className="p-2 rounded-full border border-shariti-border bg-white hover:bg-slate-50 transition"
                            title="Actualiser"
                        >
                            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>

                {loading && tickets.length === 0 ? (
                    <div className="py-12 text-center text-sm text-slate-400">Chargement…</div>
                ) : tickets.length === 0 ? (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-8 text-center text-sm text-emerald-800">
                        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                        {showResolved ? "Aucun ticket résolu à ce jour." : "Aucun ticket à arbitrer. Zéro ambiguïté."}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {tickets.map(t => (
                            <div
                                key={t.id}
                                data-testid={`pending-ticket-${t.id}`}
                                className="rounded-xl border border-shariti-border bg-white p-4"
                            >
                                <div className="flex items-start justify-between gap-4 mb-3">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <Clock className="w-4 h-4 text-shariti-orange" />
                                            <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                                                {t.target_kind === "candidate" ? "Candidat" : "Permanent"} · {t.created_by_source}
                                            </div>
                                        </div>
                                        <h3 className="text-base font-black text-shariti-black">
                                            {t.target_first_name} {t.target_last_name}
                                        </h3>
                                        <p className="text-xs text-slate-500">{t.target_email} · créé le {fmtDate(t.created_at)}</p>
                                    </div>
                                    {t.status !== "open" && (
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${t.resolved_action === "rejected" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
                                            {t.resolved_action || t.status}
                                            {t.resolved_code && ` · ${t.resolved_code}`}
                                        </span>
                                    )}
                                </div>

                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                                    Propositions ({(t.proposals || []).length})
                                </div>
                                <div className="space-y-2">
                                    {(t.proposals || []).map((p, idx) => (
                                        <div key={idx} className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                                            <CodeRdBadge code={p.code} size="sm" />
                                            <div className="flex-1">
                                                <div className="text-sm text-slate-800 font-semibold">
                                                    {p.prenom_historique} {p.nom_historique}
                                                </div>
                                                <div className="text-[11px] text-slate-500">{p.reason}</div>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${confidenceColor[p.confidence] || "bg-slate-100"}`}>
                                                {p.confidence}
                                            </span>
                                            {t.status === "open" && (
                                                <button
                                                    type="button"
                                                    onClick={() => onValidate(t, p.code)}
                                                    data-testid={`pending-validate-${t.id}-${p.code}`}
                                                    className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-wider transition"
                                                >
                                                    Valider
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {t.status === "open" && (
                                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => onReject(t)}
                                            data-testid={`pending-reject-${t.id}`}
                                            className="px-3 py-1.5 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-black uppercase tracking-wider transition"
                                        >
                                            Tout refuser
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onMint(t)}
                                            data-testid={`pending-mint-${t.id}`}
                                            className="px-3 py-1.5 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 text-white text-xs font-black uppercase tracking-wider transition"
                                        >
                                            Minter nouveau code
                                        </button>
                                    </div>
                                )}

                                {t.admin_note && (
                                    <div className="mt-2 text-[11px] text-slate-500 italic">
                                        Note : {t.admin_note}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}


// ---------------------------------------------------------- Backfill registre a posteriori (Passe C v2)

function BackfillRegistreTab({ onRefreshCounter }) {
    const [scanning, setScanning] = useState(false);
    const [applying, setApplying] = useState(false);
    const [proposals, setProposals] = useState([]);
    const [selected, setSelected] = useState({});
    const [observation, setObservation] = useState(
        "attribution pré-Passe C v2, ligne créée a posteriori"
    );
    const [result, setResult] = useState(null);

    async function runScan() {
        setScanning(true);
        setResult(null);
        try {
            const { data } = await api.get("/admin/code-rd/registry/backfill/scan");
            setProposals(data.proposals || []);
            setSelected(Object.fromEntries((data.proposals || []).map((p) => [p.code, true])));
            toast.success(`${data.total} fiche(s) sans ligne registre détectée(s)`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scan impossible");
        } finally {
            setScanning(false);
        }
    }

    async function runApply() {
        const picked = proposals.filter((p) => selected[p.code]);
        if (picked.length === 0) {
            toast.error("Sélectionne au moins une ligne à créer");
            return;
        }
        setApplying(true);
        try {
            const { data } = await api.post("/admin/code-rd/registry/backfill/apply", {
                proposals: picked.map((p) => ({
                    target_kind: p.target_kind,
                    target_id: p.target_id,
                    code: p.code,
                })),
                observation,
            });
            setResult(data);
            toast.success(`${data.applied} ligne(s) créée(s) · compteur → ${data.counter_next_after}`);
            onRefreshCounter?.();
            await runScan();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Application impossible");
        } finally {
            setApplying(false);
        }
    }

    const pickedCount = proposals.filter((p) => selected[p.code]).length;

    return (
        <div className="space-y-6" data-testid="backfill-registre-tab">
            <div className="bg-white border border-shariti-border rounded-3xl p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h3 className="text-lg font-black text-shariti-black">Rattrapage registre a posteriori</h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            Détecte les fiches qui portent un <code className="text-shariti-orange">code_rd</code> sans ligne registre.
                            Utile après un déploiement où l'attribution a été inscrite sur la fiche mais pas répercutée au registre
                            (schéma pré-Passe C v2). Lecture fiches → registre, jamais l'inverse.
                        </p>
                    </div>
                    <button
                        onClick={runScan}
                        disabled={scanning}
                        data-testid="backfill-scan-btn"
                        className="px-4 py-2 rounded-full bg-shariti-orange text-white text-xs font-black uppercase tracking-wider hover:bg-shariti-orange/90 disabled:opacity-50"
                    >
                        {scanning ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1.5" /> : <Search className="w-3.5 h-3.5 inline mr-1.5" />}
                        Scanner
                    </button>
                </div>

                {proposals.length > 0 && (
                    <>
                        <div className="mt-4 border-t border-shariti-border pt-4">
                            <label className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1 block">
                                Observation à graver dans chaque ligne créée
                            </label>
                            <input
                                type="text"
                                value={observation}
                                onChange={(e) => setObservation(e.target.value)}
                                data-testid="backfill-observation-input"
                                className="w-full border border-shariti-border rounded-lg px-3 py-2 text-sm font-mono"
                            />
                        </div>

                        <table className="w-full mt-4 text-sm">
                            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-shariti-border">
                                <tr>
                                    <th className="text-left py-2 w-8">
                                        <input
                                            type="checkbox"
                                            checked={pickedCount === proposals.length}
                                            onChange={(e) => setSelected(
                                                Object.fromEntries(proposals.map((p) => [p.code, e.target.checked]))
                                            )}
                                            data-testid="backfill-select-all"
                                        />
                                    </th>
                                    <th className="text-left py-2">Code</th>
                                    <th className="text-left py-2">Type</th>
                                    <th className="text-left py-2">Cible</th>
                                    <th className="text-left py-2">Attribué le</th>
                                    <th className="text-left py-2">Test</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposals.map((p) => (
                                    <tr key={p.code} className="border-b border-shariti-border/50" data-testid={`backfill-row-${p.code}`}>
                                        <td className="py-2">
                                            <input
                                                type="checkbox"
                                                checked={!!selected[p.code]}
                                                onChange={(e) => setSelected((s) => ({ ...s, [p.code]: e.target.checked }))}
                                                data-testid={`backfill-check-${p.code}`}
                                            />
                                        </td>
                                        <td className="py-2"><CodeRdBadge code={p.code} size="sm" /></td>
                                        <td className="py-2 text-slate-600">{p.target_kind}</td>
                                        <td className="py-2">
                                            <div className="font-semibold text-shariti-black">{p.target_name}</div>
                                            <div className="text-xs text-slate-500">{p.target_email}</div>
                                        </td>
                                        <td className="py-2 text-slate-500 text-xs">{fmtDate(p.attributed_at)}</td>
                                        <td className="py-2">
                                            {p.is_test && (
                                                <span className="inline-block text-[10px] font-black uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-300 rounded-full px-2 py-0.5">TEST</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="mt-4 flex items-center justify-between">
                            <div className="text-xs text-slate-500">
                                {pickedCount} / {proposals.length} sélectionné(s) · prévisualisation active — aucune ligne n'est encore créée
                            </div>
                            <button
                                onClick={runApply}
                                disabled={applying || pickedCount === 0}
                                data-testid="backfill-apply-btn"
                                className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-black uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {applying ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5" />}
                                Créer les {pickedCount} ligne(s) registre
                            </button>
                        </div>
                    </>
                )}

                {proposals.length === 0 && !scanning && !result && (
                    <div className="mt-4 text-sm text-slate-400 italic">
                        Aucune détection. Clique sur <b>Scanner</b> pour démarrer.
                    </div>
                )}

                {result && (
                    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="backfill-result">
                        <div className="text-sm font-black text-emerald-900">
                            {result.applied} ligne(s) créée(s) · compteur porté à <span className="font-mono">AT{String(result.counter_next_after).padStart(4, "0")}</span>
                        </div>
                        <details className="mt-2 text-xs text-emerald-800">
                            <summary className="cursor-pointer">Détails</summary>
                            <pre className="mt-1 font-mono overflow-x-auto">{JSON.stringify(result.results, null, 2)}</pre>
                        </details>
                    </div>
                )}
            </div>
        </div>
    );
}


// ---------------------------------------------------------- Diagnostic par code (Passe C v2)

function DiagnosticCodeTab() {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [coherence, setCoherence] = useState(null);
    const [loadingCoherence, setLoadingCoherence] = useState(false);

    async function runInvestigate(overrideCode) {
        const c = (overrideCode || code || "").trim().toUpperCase();
        if (!c) {
            toast.error("Entre un code (ex: AT0295)");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.get(`/admin/code-rd/diagnostics/code/${encodeURIComponent(c)}`);
            setResult(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Diagnostic impossible");
        } finally {
            setLoading(false);
        }
    }

    async function runCoherence() {
        setLoadingCoherence(true);
        setCoherence(null);
        try {
            const { data } = await api.get("/admin/code-rd/diagnostics/coherence");
            setCoherence(data);
            const ok = data.coherence_next?.delta === 0
                && (data.duplicate_holders || []).length === 0;
            toast.success(ok ? "Balayage OK · 0 doublon · delta compteur=0" : "Balayage : anomalies détectées");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Balayage impossible");
        } finally {
            setLoadingCoherence(false);
        }
    }

    async function copyJson() {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
            toast.success("JSON copié");
        } catch {
            toast.error("Copie impossible");
        }
    }

    return (
        <div className="space-y-6" data-testid="diagnostic-tab">
            <div className="bg-white border border-shariti-border rounded-3xl p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h3 className="text-lg font-black text-shariti-black">Diagnostic par code</h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            Investigation lecture seule : ligne registre + fiche rattachée + contrats + snapshot d'audit.
                            Utile pour tracer un code contesté (ex : AT0537 doublon, AT0295 canonique) ou vérifier
                            l'état d'un code brûlé.
                        </p>
                    </div>
                    <button
                        onClick={runCoherence}
                        disabled={loadingCoherence}
                        data-testid="diagnostic-coherence-btn"
                        className="px-4 py-2 rounded-full bg-shariti-black text-white text-xs font-black uppercase tracking-wider hover:bg-shariti-black/85 disabled:opacity-50"
                    >
                        {loadingCoherence ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1.5" /> : <ShieldAlert className="w-3.5 h-3.5 inline mr-1.5" />}
                        Balayage cohérence
                    </button>
                </div>

                <div className="flex items-center gap-2 mt-4">
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === "Enter" && runInvestigate()}
                        placeholder="AT0295, ATO184, AT0537..."
                        data-testid="diagnostic-code-input"
                        className="w-64 border border-shariti-border rounded-lg px-3 py-2 text-sm font-mono uppercase"
                    />
                    <button
                        onClick={() => runInvestigate()}
                        disabled={loading}
                        data-testid="diagnostic-code-btn"
                        className="px-4 py-2 rounded-full bg-shariti-orange text-white text-xs font-black uppercase tracking-wider hover:bg-shariti-orange/90 disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1.5" /> : <Search className="w-3.5 h-3.5 inline mr-1.5" />}
                        Investiguer
                    </button>
                    {["AT0295", "AT0537", "ATO184", "AT0184", "AT0541"].map((c) => (
                        <button
                            key={c}
                            onClick={() => { setCode(c); runInvestigate(c); }}
                            data-testid={`diagnostic-quick-${c}`}
                            className="px-3 py-1.5 rounded-full bg-shariti-cream border border-shariti-border text-xs font-mono hover:bg-white"
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {result && (
                    <div className="mt-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CodeRdBadge code={result.code} size="lg" />
                                {result.registry_row?.statut === "reserved" && (
                                    <span className="text-[10px] font-black uppercase tracking-wider text-red-700 bg-red-100 border border-red-300 rounded-full px-2 py-0.5" data-testid="diagnostic-badge-reserved">
                                        RESERVED
                                    </span>
                                )}
                                {result.registry_row?.statut === "attributed" && !result.candidate && !result.permanent && (
                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300 rounded-full px-2 py-0.5" data-testid="diagnostic-badge-unlinked">
                                        Attribué · non rattaché
                                    </span>
                                )}
                                {(result.candidate || result.permanent) && (
                                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-2 py-0.5" data-testid="diagnostic-badge-linked">
                                        Rattaché
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={copyJson}
                                data-testid="diagnostic-copy-json"
                                className="px-3 py-1.5 rounded-full bg-shariti-cream border border-shariti-border text-xs font-black uppercase tracking-wider hover:bg-white"
                            >
                                <ClipboardCopy className="w-3.5 h-3.5 inline mr-1" />
                                Copier JSON
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-shariti-cream border border-shariti-border rounded-2xl p-4" data-testid="diagnostic-registry">
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Ligne registre</div>
                                {result.registry_row ? (
                                    <dl className="text-sm space-y-1">
                                        <div><dt className="inline text-slate-500">Statut : </dt><dd className="inline font-mono">{result.registry_row.statut}</dd></div>
                                        <div><dt className="inline text-slate-500">Numeric : </dt><dd className="inline font-mono">{result.registry_row.numeric}</dd></div>
                                        <div><dt className="inline text-slate-500">Name key : </dt><dd className="inline font-mono">{result.registry_row.name_key || "—"}</dd></div>
                                        <div><dt className="inline text-slate-500">Historique : </dt><dd className="inline">{result.registry_row.prenom_historique} {result.registry_row.nom_historique}</dd></div>
                                        <div><dt className="inline text-slate-500">Candidate ID : </dt><dd className="inline font-mono text-xs">{result.registry_row.candidate_id || "—"}</dd></div>
                                        {result.registry_row.observation && (
                                            <div className="pt-2 border-t border-shariti-border/50 mt-2">
                                                <dt className="text-xs uppercase tracking-wider text-slate-500 mb-1">Observation</dt>
                                                <dd className="text-xs italic text-slate-700">{result.registry_row.observation}</dd>
                                            </div>
                                        )}
                                    </dl>
                                ) : (
                                    <div className="text-sm text-slate-400 italic">Aucune ligne registre</div>
                                )}
                            </div>

                            <div className="bg-shariti-cream border border-shariti-border rounded-2xl p-4" data-testid="diagnostic-holder">
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Fiche rattachée</div>
                                {result.candidate ? (
                                    <dl className="text-sm space-y-1">
                                        <div><dt className="inline text-slate-500">Type : </dt><dd className="inline font-mono">candidate</dd></div>
                                        <div><dt className="inline text-slate-500">Nom : </dt><dd className="inline font-semibold">{result.candidate.first_name} {result.candidate.last_name}</dd></div>
                                        <div><dt className="inline text-slate-500">Email : </dt><dd className="inline">{result.candidate.email}</dd></div>
                                        <div><dt className="inline text-slate-500">Statut : </dt><dd className="inline font-mono">{result.candidate.status}</dd></div>
                                        <div><dt className="inline text-slate-500">ID : </dt><dd className="inline font-mono text-xs">{result.candidate.id}</dd></div>
                                    </dl>
                                ) : result.permanent ? (
                                    <dl className="text-sm space-y-1">
                                        <div><dt className="inline text-slate-500">Type : </dt><dd className="inline font-mono">permanent</dd></div>
                                        <div><dt className="inline text-slate-500">Nom : </dt><dd className="inline font-semibold">{result.permanent.first_name} {result.permanent.last_name}</dd></div>
                                        <div><dt className="inline text-slate-500">Email : </dt><dd className="inline">{result.permanent.email}</dd></div>
                                        <div><dt className="inline text-slate-500">ID : </dt><dd className="inline font-mono text-xs">{result.permanent.id}</dd></div>
                                    </dl>
                                ) : (
                                    <div className="text-sm text-slate-400 italic">Aucune fiche rattachée</div>
                                )}
                                <div className="pt-2 border-t border-shariti-border/50 mt-2 text-xs text-slate-500">
                                    Contrats : <span className="font-mono">{result.contracts?.total ?? 0}</span> · signés/actifs : <span className="font-mono">{result.contracts?.signed_or_active ?? 0}</span>
                                </div>
                            </div>
                        </div>

                        <details className="bg-slate-50 border border-shariti-border rounded-2xl p-4" data-testid="diagnostic-raw-json">
                            <summary className="cursor-pointer text-xs font-black uppercase tracking-wider text-slate-500">JSON brut</summary>
                            <pre className="mt-2 text-xs font-mono overflow-x-auto text-slate-700">{JSON.stringify(result, null, 2)}</pre>
                        </details>

                        {/* Iter82 · Rebranchement & Brûlage (fix Bogdan/AT0536-AT0550) */}
                        {(result.candidate || result.permanent) && (
                            <ReassignForm
                                currentCode={code}
                                candidate={result.candidate || result.permanent}
                                onDone={() => runInvestigate(code)}
                            />
                        )}
                    </div>
                )}

                {coherence && (
                    <div className="mt-6 bg-white border border-shariti-border rounded-2xl p-4" data-testid="coherence-result">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-black text-shariti-black">Balayage cohérence · {fmtDate(coherence.captured_at)}</div>
                            <div className={`text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full ${
                                coherence.coherence_next?.delta === 0 && (coherence.duplicate_holders || []).length === 0
                                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                    : "bg-red-100 text-red-800 border border-red-300"
                            }`} data-testid="coherence-ok-badge">
                                {coherence.coherence_next?.delta === 0 && (coherence.duplicate_holders || []).length === 0 ? "OK" : "Anomalies"}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            <div className="bg-shariti-cream border border-shariti-border rounded-lg p-3">
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Compteur</div>
                                <div className="font-mono">
                                    actuel <b>AT{String(coherence.coherence_next?.next_actual).padStart(4, "0")}</b>
                                    <br />attendu <b>AT{String(coherence.coherence_next?.next_expected).padStart(4, "0")}</b>
                                    <br />delta {coherence.coherence_next?.delta}
                                </div>
                            </div>
                            <div className="bg-shariti-cream border border-shariti-border rounded-lg p-3">
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Doublons</div>
                                <div className="font-mono">{(coherence.duplicate_holders || []).length}</div>
                            </div>
                            <div className="bg-shariti-cream border border-shariti-border rounded-lg p-3">
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Trous inattendus</div>
                                <div className="font-mono">{coherence.holes_unexpected?.count ?? 0}</div>
                                {coherence.holes_unexpected?.codes?.length > 0 && (
                                    <div className="text-xs text-slate-500 mt-1 font-mono">
                                        {coherence.holes_unexpected.codes.slice(0, 10).join(", ")}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-slate-500 italic">
                            {coherence.holes_unexpected?.note}
                        </div>
                        {(coherence.holders_above_545 || []).length > 0 && (
                            <div className="mt-3">
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">Détenteurs ≥ AT0545</div>
                                <table className="w-full text-xs">
                                    <thead className="text-slate-500">
                                        <tr>
                                            <th className="text-left py-1">Code</th>
                                            <th className="text-left py-1">Détenteur</th>
                                            <th className="text-left py-1">Type</th>
                                            <th className="text-left py-1">Mode</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(coherence.holders_above_545 || []).map((h, i) => (
                                            <tr key={i} className="border-t border-shariti-border/50" data-testid={`coherence-holder-${h.code}`}>
                                                <td className="py-1"><CodeRdBadge code={h.code} size="sm" /></td>
                                                <td className="py-1">
                                                    <div className="font-semibold">{h.full_name || <span className="italic text-slate-400">—</span>}</div>
                                                    {h.email && <div className="text-slate-400 text-[10px]">{h.email}</div>}
                                                </td>
                                                <td className="py-1 font-mono text-slate-500">
                                                    {h.entity}
                                                    {h.is_test && <span className="ml-1 text-orange-600">·test</span>}
                                                </td>
                                                <td className="py-1 font-mono text-slate-500">{h.mode || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


// ---------------------------------------------------------- Page shell

export default function CodeRdAdmin() {
    const [counter, setCounter] = useState(null);
    const [activeTab, setActiveTab] = useState("registre");

    async function fetchCounter() {
        try {
            const { data } = await api.get("/admin/code-rd/counter");
            setCounter(data);
        } catch (err) {
            // silent — counter is just a UI hint
        }
    }
    useEffect(() => { fetchCounter(); }, []);

    return (
        <div className="min-h-screen bg-shariti-cream">
            {/* Sticky header — mirrors Embauche.jsx pattern */}
            <div className="sticky top-0 z-20 bg-shariti-cream/95 backdrop-blur-sm border-b border-shariti-border">
                <div className="max-w-[1600px] mx-auto px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-shariti-orange-soft border border-shariti-orange/30 flex items-center justify-center">
                            <Fingerprint className="w-5 h-5 text-shariti-orange" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black tracking-tight text-shariti-black">Codes RD</h1>
                            <p className="text-sm text-slate-500 mt-0.5">
                                Matricules Shariti · source unique de vérité (PayFit · Booking · Reporting)
                                {counter?.next_code && (
                                    <> · prochain code <span className="font-mono font-black text-shariti-orange">{counter.next_code}</span></>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto px-6 py-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="bg-white border border-shariti-border h-auto p-1 rounded-full">
                        <TabsTrigger value="registre" data-testid="code-rd-tab-btn-registre" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            Registre & attribution
                        </TabsTrigger>
                        <TabsTrigger value="rattrapage" data-testid="code-rd-tab-btn-rattrapage" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            Rattrapage hired
                        </TabsTrigger>
                        <TabsTrigger value="pending" data-testid="code-rd-tab-btn-pending" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            <Clock className="w-3.5 h-3.5 inline-block mr-1" />
                            File d'attente
                        </TabsTrigger>
                        <TabsTrigger value="backfill" data-testid="code-rd-tab-btn-backfill" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            <History className="w-3.5 h-3.5 inline-block mr-1" />
                            Registre a posteriori
                        </TabsTrigger>
                        <TabsTrigger value="diagnostic" data-testid="code-rd-tab-btn-diagnostic" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            <FileText className="w-3.5 h-3.5 inline-block mr-1" />
                            Diagnostic par code
                        </TabsTrigger>
                        <TabsTrigger value="tokens" data-testid="code-rd-tab-btn-tokens" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            Jetons de service
                        </TabsTrigger>
                        <TabsTrigger value="journal" data-testid="code-rd-tab-btn-journal" className="rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider data-[state=active]:bg-shariti-orange data-[state=active]:text-white">
                            Journal API
                        </TabsTrigger>
                    </TabsList>

                    <div className="mt-6">
                        <TabsContent value="registre">
                            <RegistreTab counter={counter} onRefreshCounter={fetchCounter} />
                        </TabsContent>
                        <TabsContent value="rattrapage">
                            <RattrapageTab
                                onRefreshCounter={fetchCounter}
                                onGoToArbitrage={() => setActiveTab("pending")}
                            />
                        </TabsContent>
                        <TabsContent value="pending">
                            <PendingReviewTab onRefreshCounter={fetchCounter} />
                        </TabsContent>
                        <TabsContent value="backfill">
                            <BackfillRegistreTab onRefreshCounter={fetchCounter} />
                        </TabsContent>
                        <TabsContent value="diagnostic">
                            <DiagnosticCodeTab />
                        </TabsContent>
                        <TabsContent value="tokens">
                            <TokensTab />
                        </TabsContent>
                        <TabsContent value="journal">
                            <JournalTab />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </div>
    );
}



/**
 * Iter82 · Rebranchement candidat ↔ code + brûlage
 *
 * Cas d'usage (CEO brief 2026-08-03) : le candidat est rattaché à un
 * code AUTO-attribué (ex : AT0550) alors qu'il aurait dû prendre un
 * code du registre historique (ex : AT0536). Ce formulaire permet à
 * l'admin de :
 *   1. Rebrancher le candidat sur target_code (rattache dans le registre).
 *   2. Brûler l'ancien code (le passer en `reserved` avec motif).
 *   3. Ajouter un alias sur target_code pour éviter la récidive fuzzy.
 */
function ReassignForm({ currentCode, candidate, onDone }) {
    const [expanded, setExpanded] = useState(false);
    const [targetCode, setTargetCode] = useState("");
    const [alias, setAlias] = useState("");
    const [observation, setObservation] = useState("");
    const [burnSource, setBurnSource] = useState(true);
    const [busy, setBusy] = useState(false);

    async function submit() {
        const tgt = (targetCode || "").trim().toUpperCase();
        if (!tgt) {
            toast.error("Renseigne le code cible (ex: AT0536)");
            return;
        }
        if ((observation || "").trim().length < 8) {
            toast.error("Observation obligatoire (min 8 caractères)");
            return;
        }
        try {
            setBusy(true);
            const payload = {
                candidate_id: candidate.id,
                target_code: tgt,
                observation: observation.trim(),
            };
            if (alias.trim()) payload.alias_to_add = alias.trim();
            if (burnSource && currentCode) payload.source_code_to_burn = currentCode;

            const { data } = await api.post(
                "/admin/code-rd/registry/reassign",
                payload,
            );
            const parts = [`Rebranché sur ${data.target_code}`];
            if (data.burnt) parts.push(`${data.burnt.code} → reserved`);
            if (data.alias_added) parts.push(`alias "${data.alias_added}" ajouté`);
            toast.success("Rebranchement terminé ✨", {
                description: parts.join(" · "),
                duration: 10000,
            });
            setExpanded(false);
            setTargetCode(""); setAlias(""); setObservation("");
            onDone?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Rebranchement impossible");
        } finally {
            setBusy(false);
        }
    }

    if (!expanded) {
        return (
            <button
                type="button"
                onClick={() => setExpanded(true)}
                data-testid="diagnostic-reassign-open"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-red-300 bg-red-50 text-red-800 text-xs font-black uppercase tracking-widest hover:bg-red-100 transition"
            >
                <ArrowRightLeft className="w-4 h-4" />
                Rebrancher vers un autre code + brûler celui-ci
            </button>
        );
    }

    return (
        <div className="mt-3 rounded-2xl border-2 border-red-300 bg-red-50 p-4 space-y-3" data-testid="diagnostic-reassign-form">
            <div className="flex items-center justify-between">
                <div className="text-sm font-black text-red-900 inline-flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4" />
                    Rebrancher {candidate.first_name} {candidate.last_name} vers un autre code
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    disabled={busy}
                    className="text-xs font-bold text-red-700 hover:text-red-900"
                >
                    Annuler
                </button>
            </div>
            <div className="text-xs text-red-800 leading-snug">
                Cette action est <strong>tracée URSSAF</strong>. Le candidat sera lié au code
                cible, l&apos;ancien code sera brûlé (status <code className="text-[10px]">reserved</code>)
                pour ne plus jamais être ré-attribué.
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-red-800">Code cible (ex: AT0536)</label>
                    <input
                        type="text"
                        value={targetCode}
                        onChange={(e) => setTargetCode(e.target.value.toUpperCase())}
                        placeholder="AT0536"
                        data-testid="diagnostic-reassign-target"
                        className="mt-1 w-full border border-red-300 rounded-lg px-3 py-2 text-sm font-mono uppercase bg-white"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-red-800">Alias à ajouter (nom d&apos;usage terrain)</label>
                    <input
                        type="text"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                        placeholder="Ex: Dimitri Penard"
                        data-testid="diagnostic-reassign-alias"
                        className="mt-1 w-full border border-red-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                </div>
            </div>

            <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-red-800">Motif URSSAF (obligatoire, min 8 caractères)</label>
                <textarea
                    rows={2}
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    placeholder="Ex : Le nom d'usage terrain diffère du nom légal → AT0550 auto-attribué à tort, canonique historique = AT0536."
                    data-testid="diagnostic-reassign-observation"
                    className="mt-1 w-full border border-red-300 rounded-lg px-3 py-2 text-sm bg-white resize-y"
                />
            </div>

            <label className="flex items-center gap-2 text-xs text-red-800">
                <input
                    type="checkbox"
                    checked={burnSource}
                    onChange={(e) => setBurnSource(e.target.checked)}
                    data-testid="diagnostic-reassign-burn"
                />
                <Flame className="w-3 h-3" />
                Brûler <code className="text-[10px] font-mono">{currentCode || "—"}</code> après rebranchement
                (le passe en <code className="text-[10px]">reserved</code>, plus jamais ré-attribué)
            </label>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy}
                    data-testid="diagnostic-reassign-submit"
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-600 text-white text-xs font-black uppercase tracking-widest hover:bg-red-700 disabled:opacity-50 transition"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                    Rebrancher & tracer
                </button>
            </div>
        </div>
    );
}
